import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, message, Pagination, Select, Tabs, Switch, Space, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import HeaderComponent from '../../components/header/headerComponent';
import SearchSvg from '../../assets/images/SearchSvg';
import { ExportExcelSvg, ExportExcelListSvg, RevokeSvg } from '../../assets/images/HeaderSvg';
import * as XLSX from 'xlsx-js-style';
import TableFetchData from '../../components/data-table/TableFetchData';
import MessageConfirmModal from '../../components/messageModal/MessageConfirmModal';
import {
  EVENTS_URL,
  EVENT_SUBMISSIONS_URL,
  EVENT_CODE_BATCHES_URL,
  EVENT_CODE_BATCHES_USED_URL,
} from '../../api/URLs';
import { getDataRequest, patchDataRequest } from '../../api/serviceMethods';
import { formatDateTime } from '../../utils/dateFormatUtils';
import CreateInviteBatchModal from './CreateInviteBatchModal';
import '../../components/dashboard/tabs.css';
import '../../styles/event.css';

const DEFAULT_POSTER =
  'https://fastly.picsum.photos/id/30/1280/901.jpg?hmac=A_hpFyEavMBB7Dsmmp53kPXKmatwM05MUDatlWSgATE';

const formatDatePart = (isoStr) => {
  if (!isoStr) return '';
  try {
    const dt = new Date(isoStr);
    if (isNaN(dt.getTime())) return isoStr;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}.${m}.${d}`;
  } catch {
    return isoStr;
  }
};

export const normalizeSubmission = (item, currentLang = 'cn') => {
  if (!item) return null;

  const locObj =
    item.localize ||
    item.submission?.localize ||
    item.submissionsId?.localize ||
    item.submissionId?.localize ||
    item.eventsId?.localize ||
    {};

  let layout = [];
  if (Array.isArray(item.layout)) {
    layout = item.layout;
  } else if (Array.isArray(item.submission?.layout)) {
    layout = item.submission.layout;
  }

  const candidateBlocks = [
    locObj['cn'],
    locObj[currentLang],
    locObj['en'],
    ...Object.values(locObj),
  ].filter(Boolean);

  if (layout.length === 0) {
    for (const block of candidateBlocks) {
      if (
        Array.isArray(block.layout) &&
        block.layout.some((q) =>
          Array.isArray(q.answers) ? q.answers.length > 0 : q.answers !== null && q.answers !== undefined && q.answers !== ''
        )
      ) {
        layout = block.layout;
        break;
      }
    }
  }

  if (layout.length === 0) {
    const fallbackBlock =
      locObj[currentLang] || locObj['cn'] || locObj['en'] || Object.values(locObj)[0] || {};
    layout = Array.isArray(fallbackBlock.layout) ? fallbackBlock.layout : [];
  }

  const getAnswerStr = (answers) => {
    if (Array.isArray(answers)) {
      return answers
        .filter((a) => a !== null && a !== undefined && a !== '')
        .map((a) => String(a).trim())
        .filter(Boolean)
        .join(', ');
    }
    if (answers !== null && answers !== undefined && answers !== '') {
      return String(answers).trim();
    }
    return '';
  };

  const extractPhone = (q) => {
    const answerStr = getAnswerStr(q.answers);
    if (!answerStr) return '';

    let selectedCc = '';
    if (Array.isArray(q.countryCodes)) {
      const answeredItem = q.countryCodes.find(
        (c) =>
          c &&
          (c.isanswer === true ||
            c.isanswer === 'true' ||
            String(c.isanswer).toLowerCase() === 'true')
      );
      if (answeredItem) {
        selectedCc = String(answeredItem.value || answeredItem.label || '').trim();
      }
    }
    if (!selectedCc && q.defaultCountryCode) {
      selectedCc = String(q.defaultCountryCode).trim();
    }
    if (selectedCc && !selectedCc.startsWith('+') && /^\d+$/.test(selectedCc)) {
      selectedCc = `+${selectedCc}`;
    }

    if (answerStr.startsWith('+')) {
      return answerStr;
    }
    if (selectedCc) {
      if (answerStr.startsWith(selectedCc)) {
        return answerStr;
      }
      return `${selectedCc} ${answerStr}`.trim();
    }
    return answerStr;
  };

  let name = '';
  let phone = '';
  let company = '';
  let email = '';
  let code = '';

  const processLayout = (lay) => {
    if (!Array.isArray(lay)) return;
    lay.forEach((q) => {
      const title = String(q.title || '').trim().toLowerCase();
      const qType = String(q.questionType || '').toLowerCase();
      const qCode = String(q.code || '').trim().toUpperCase();
      const answerStr = getAnswerStr(q.answers);

      // 1. Phone Question (check isanswer for countryCode)
      if (
        !phone &&
        (qType === 'phone' ||
          title.includes('手机') ||
          title.includes('电话') ||
          title.includes('phone') ||
          title.includes('mobile'))
      ) {
        const extracted = extractPhone(q);
        if (extracted) phone = extracted;
      }

      // 2. Email Question (get email from answers)
      if (
        !email &&
        (qType === 'email' ||
          title.includes('电邮') ||
          title.includes('邮箱') ||
          title.includes('email'))
      ) {
        if (answerStr) email = answerStr;
      }

      // 3. Name Question
      if (
        !name &&
        (title.includes('姓名') ||
          title.includes('name') ||
          (qType === 'text' && title === '姓名'))
      ) {
        if (answerStr) name = answerStr;
      }

      // 4. Company / Salon Question
      if (
        !company &&
        (title.includes('发廊') ||
          title.includes('公司') ||
          title.includes('salon') ||
          title.includes('company'))
      ) {
        if (answerStr) company = answerStr;
      }

      // 5. Invitation Code Question
      if (
        !code &&
        (qCode === 'INVITATION-CODE' ||
          title.includes('邀请码') ||
          title.includes('invite') ||
          title.includes('code'))
      ) {
        if (answerStr) code = answerStr;
      }
    });
  };

  processLayout(layout);

  if (!phone || !email || !name || !company || !code) {
    for (const block of candidateBlocks) {
      if (Array.isArray(block.layout) && block.layout !== layout) {
        processLayout(block.layout);
        if (phone && email && name && company && code) break;
      }
    }
  }

  // Fallback for name
  if (!name) {
    name = item.member?.name || item.name || '';
  }

  // Fallback for email
  if (!email) {
    email = item.member?.email || item.email || '';
  }

  // Fallback for company
  if (!company) {
    company = item.member?.company || item.company || '';
  }

  // Fallback for phone
  if (!phone) {
    const pSource = item.member?.phone || item.phone;
    if (pSource) {
      if (typeof pSource === 'object') {
        const cc = pSource.countryCode
          ? `${String(pSource.countryCode).startsWith('+') ? '' : '+'}${pSource.countryCode} `
          : '';
        const num = pSource.number || '';
        phone = `${cc}${num}`.trim();
      } else {
        phone = String(pSource).trim();
      }
    }
  }

  // Fallback for code
  if (!code && item.localize?.cn?.code) {
    code = item.localize.cn.code;
  }
  if (!code && item.code) {
    code = item.code;
  } else if (!code && item.couponCode) {
    code = item.couponCode;
  } else if (!code && item.ticketCode) {
    code = item.ticketCode;
  }

  const finalCode = code || '-';
  const regTime = item.createdTS ? formatDateTime(item.createdTS) : '-';
  const admissionTime = item.admissionTime
    ? formatDateTime(item.admissionTime)
    : item.isCheckedIn && item.updatedTS
      ? formatDateTime(item.updatedTS)
      : '-';

  return {
    ...item,
    id: item._id,
    name: name || '-',
    phone: phone || '-',
    company: company || '-',
    code: finalCode,
    email: email || '-',
    regTime,
    admissionTime,
    admissionStatus: item.isCheckedIn ? 'admitted' : (item.admissionStatus || 'waiting'),
    localize: {
      ...item.localize,
      cn: {
        ...item.localize?.cn,
        code: finalCode,
      },
    },
  };
};

const EventApprovalPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const navEvent = location.state?.event;
  const [eventDetail, setEventDetail] = useState(navEvent || null);
  const [eventLoading, setEventLoading] = useState(false);

  const fetchEventDetail = useCallback(async () => {
    const targetId = id || location.state?.event?.eventId || location.state?.event?._id || location.state?.event?.id;
    if (!targetId) return;
    try {
      setEventLoading(true);
      const res = await getDataRequest(`${EVENTS_URL}/${targetId}`);
      setEventLoading(false);
      if (res && (res.status === 200 || res.data)) {
        const data = res.data || res;
        setEventDetail(data);
      }
    } catch (err) {
      setEventLoading(false);
      console.error('Error fetching event detail:', err);
    }
  }, [id, location.state?.event]);

  useEffect(() => {
    fetchEventDetail();
  }, [fetchEventDetail]);

  const effectiveEventId = eventDetail?._id || id || eventDetail?.eventId;

  // Submissions state
  const [rawSubmissions, setRawSubmissions] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [totalSubmissions, setTotalSubmissions] = useState(0);

  const [approvalTab, setApprovalTab] = useState('pending'); // 'pending' (Registration Approval) | 'confirmed' (Invite Number)
  const [searchKeyword, setSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [jumpPage, setJumpPage] = useState('1');

  const messageModalRef = useRef(null);

  // Invite Numbers state with system tableParams
  const [inviteNumbers, setInviteNumbers] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [inviteTableParams, setInviteTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
    sortOrder: undefined,
    sortField: undefined,
  });

  // Invite Detail View state
  const [inviteDetail, setInviteDetail] = useState(null); // null | { record, type: 'used' | 'unused' }
  const [detailKeyword, setDetailKeyword] = useState('');
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTableParams, setDetailTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
    sortOrder: undefined,
    sortField: undefined,
  });

  const eventTitle =
    eventDetail?.localize?.[i18n.language]?.title ||
    eventDetail?.localize?.cn?.title ||
    eventDetail?.localize?.en?.title ||
    eventDetail?.title ||
    '';
  const eventPoster =
    eventDetail?.images?.[0]?.url ||
    eventDetail?.poster ||
    DEFAULT_POSTER;
  const eventTime =
    eventDetail?.startDate && eventDetail?.endDate
      ? `${formatDatePart(eventDetail.startDate)}-${formatDatePart(eventDetail.endDate)}`
      : eventDetail?.time || '';
  const eventCreator =
    eventDetail?.createdBy?.name ||
    eventDetail?.creator ||
    'ESI System';
  const eventStatus = eventDetail?.status;
  const isExpired = Boolean(
    location.state?.tab === 'expired' ||
    location.state?.event?.isExpired ||
    location.state?.event?.tab === 'expired' ||
    eventDetail?.isExpired ||
    eventDetail?.status === 'expired' ||
    (eventDetail?.endDate && new Date(eventDetail.endDate).getTime() < new Date().setHours(0, 0, 0, 0))
  );
  const registeredCount = eventDetail?.registeredCount ?? totalSubmissions ?? 0;

  // Fetch Submissions from backend API
  const fetchSubmissions = useCallback(
    async (page = 1, size = 10, kw = '') => {
      if (!effectiveEventId) return;
      try {
        setSubmissionsLoading(true);
        const params = {
          keyword: kw !== undefined ? kw.trim() : '',
          startDate: '',
          endDate: '',
          pageNo: page,
          pageSize: size,
          eventsId: effectiveEventId,
        };
        const res = await getDataRequest(EVENT_SUBMISSIONS_URL, params);
        setSubmissionsLoading(false);
        if (res && res.status === 200 && Array.isArray(res.data)) {
          const list = res.data.map((item) => normalizeSubmission(item, i18n.language));
          setRawSubmissions(res.data);
          setSubmissions(list);
          setTotalSubmissions(typeof res.total === 'number' ? res.total : list.length);
          if (!eventDetail && res.data[0]?.eventsId && typeof res.data[0].eventsId === 'object') {
            setEventDetail(res.data[0].eventsId);
          }
        } else {
          setRawSubmissions([]);
          setSubmissions([]);
          setTotalSubmissions(0);
        }
      } catch (err) {
        setSubmissionsLoading(false);
        console.error('Error fetching event submissions:', err);
        setRawSubmissions([]);
        setSubmissions([]);
        setTotalSubmissions(0);
      }
    },
    [effectiveEventId, navEvent, eventDetail]
  );

  useEffect(() => {
    if (approvalTab === 'pending') {
      fetchSubmissions(currentPage, pageSize, searchKeyword);
    }
  }, [approvalTab, currentPage, pageSize, fetchSubmissions]);

  // When language switches, re-normalize submissions in memory without re-calling API or showing loading spinner
  useEffect(() => {
    if (rawSubmissions && rawSubmissions.length > 0) {
      setSubmissions(rawSubmissions.map((item) => normalizeSubmission(item, i18n.language)));
    }
  }, [i18n.language]);

  // Fetch Invite Code Batches from backend API
  const fetchInviteBatches = useCallback(async () => {
    try {
      setInviteLoading(true);
      const params = {
        pageNo: inviteTableParams.pagination?.current || 1,
        pageSize: inviteTableParams.pagination?.pageSize || 10,
        keyword: searchKeyword ? searchKeyword.trim() : '',
        eventId: effectiveEventId || '',
      };
      if (inviteTableParams.sortField && inviteTableParams.sortOrder) {
        params.sortKeys = {
          [inviteTableParams.sortField]: inviteTableParams.sortOrder === 'ascend' ? 'asc' : 'desc',
        };
      }
      const res = await getDataRequest(EVENT_CODE_BATCHES_URL, params);
      setInviteLoading(false);
      if (res && (res.status === 200 || Array.isArray(res.data))) {
        let list = Array.isArray(res.data) ? res.data : (res.data?.list || []);
        if (inviteTableParams.sortField && inviteTableParams.sortOrder) {
          const field = inviteTableParams.sortField;
          const order = inviteTableParams.sortOrder;
          list = [...list].sort((a, b) => {
            const valA = a[field] ?? '';
            const valB = b[field] ?? '';
            if (typeof valA === 'number' && typeof valB === 'number') {
              return order === 'ascend' ? valA - valB : valB - valA;
            }
            return order === 'ascend'
              ? String(valA).localeCompare(String(valB))
              : String(valB).localeCompare(String(valA));
          });
        }
        setInviteNumbers(list);
        setInviteTableParams((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            total: res.total !== undefined ? res.total : list.length,
          },
        }));
      }
    } catch (err) {
      setInviteLoading(false);
      console.log('Error fetching invite batches:', err);
    }
  }, [effectiveEventId, inviteTableParams.pagination?.current, inviteTableParams.pagination?.pageSize, inviteTableParams.sortField, inviteTableParams.sortOrder, searchKeyword]);

  useEffect(() => {
    if (approvalTab === 'confirmed') {
      fetchInviteBatches();
    }
  }, [
    approvalTab,
    inviteTableParams.pagination?.current,
    inviteTableParams.pagination?.pageSize,
    inviteTableParams?.sortOrder,
    inviteTableParams?.sortField,
    JSON.stringify(inviteTableParams.filters),
    fetchInviteBatches,
  ]);

  // Fetch Used / Unused Codes detail from backend API
  const fetchInviteDetails = useCallback(async () => {
    if (!inviteDetail?.record) return;
    try {
      setDetailLoading(true);
      const batchId = inviteDetail.record._id || inviteDetail.record.id;
      const params = {
        keyword: detailKeyword ? detailKeyword.trim() : '',
        status: inviteDetail.type === 'used' ? 'used' : 'unused',
        pageNo: detailTableParams.pagination?.current || 1,
        pageSize: detailTableParams.pagination?.pageSize || 10,
        batchId: batchId,
      };
      if (detailTableParams.sortField && detailTableParams.sortOrder) {
        params.sortKeys = {
          [detailTableParams.sortField]: detailTableParams.sortOrder === 'ascend' ? 'asc' : 'desc',
        };
      } else {
        params['sortKeys[usedTS]'] = 'desc';
      }
      const res = await getDataRequest(EVENT_CODE_BATCHES_USED_URL, params);
      setDetailLoading(false);
      if (res && (res.status === 200 || Array.isArray(res.data))) {
        let list = Array.isArray(res.data) ? res.data : (res.data?.list || []);
        if (detailTableParams.sortField && detailTableParams.sortOrder) {
          const field = detailTableParams.sortField;
          const order = detailTableParams.sortOrder;
          list = [...list].sort((a, b) => {
            const valA = a[field] ?? '';
            const valB = b[field] ?? '';
            return order === 'ascend'
              ? String(valA).localeCompare(String(valB))
              : String(valB).localeCompare(String(valA));
          });
        }
        setDetailData(list);
        setDetailTableParams((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            total: res.total !== undefined ? res.total : list.length,
          },
        }));
      }
    } catch (err) {
      setDetailLoading(false);
      console.log('Error fetching invite details:', err);
    }
  }, [inviteDetail, detailKeyword, detailTableParams.pagination?.current, detailTableParams.pagination?.pageSize, detailTableParams.sortField, detailTableParams.sortOrder]);

  useEffect(() => {
    if (inviteDetail) {
      fetchInviteDetails();
    }
  }, [
    inviteDetail,
    detailTableParams.pagination?.current,
    detailTableParams.pagination?.pageSize,
    detailTableParams?.sortOrder,
    detailTableParams?.sortField,
    JSON.stringify(detailTableParams.filters),
    fetchInviteDetails,
  ]);

  const handleToggleInviteStatus = async (record) => {
    const recordId = record._id || record.id;
    try {
      const res = await patchDataRequest(`${EVENT_CODE_BATCHES_URL}/${recordId}/toggle`, {
        isActive: !record.isActive,
      });
      if (res && (res.status === 200 || res.status === 204 || res.success)) {
        messageModalRef.current?.showSuccessConfirmsAutoClose((t('Invitation code status updated successfully')),
          () => { },
          '',
          true
        );
        fetchInviteBatches();
      } else {
        messageModalRef.current?.showWarningConfirmsAutoClose(
          (res && res.message) || t('An error occurred.'),
          () => { },
          '',
          true
        );
      }
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || t('Server Error!');
      messageModalRef.current?.showWarningConfirmsAutoClose(errMsg, () => { }, '', true);
    }
  };

  const handleRevokeInvite = (record) => {
    const recordId = record._id || record.id;
    messageModalRef.current?.showWarningConfirm(
      t('Are you sure you want to revoke this invite code batch?'),
      async () => {
        try {
          const res = await patchDataRequest(`${EVENT_CODE_BATCHES_URL}/${recordId}/status`, {
            status: 'revoke',
          });
          if (res && (res.status === 200 || res.status === 204 || res.success)) {
            messageModalRef.current?.showSuccessConfirmsAutoClose(
              t('Batch revoked successfully!'),
              () => { },
              '',
              true
            );
            fetchInviteBatches();
          } else {
            messageModalRef.current?.showWarningConfirmsAutoClose(
              (res && res.message) || t('An error occurred.'),
              () => { },
              '',
              true
            );
          }
        } catch (err) {
          const errMsg = err?.response?.data?.message || err?.message || t('Server Error!');
          messageModalRef.current?.showWarningConfirmsAutoClose(errMsg, () => { }, '', true);
        }
      },
      '',
      true
    );
  };

  const usedDetailColumns = [
    {
      title: <label className="titleCard14400">{t('Invitation Code')}</label>,
      dataIndex: 'couponCode',
      key: 'couponCode',
      render: (val, record) => <span style={{ color: '#262626', fontSize: 13, fontWeight: 500 }}>{val || record.code}</span>,
    },
    {
      title: <label className="titleCard14400">{t('User ID')}</label>,
      dataIndex: 'memberId',
      key: 'memberId',
      render: (val, record) => <span style={{ color: '#262626', fontSize: 13 }}>{record.member?.memberId || record.memberId || val || '-'}</span>,
    },
    {
      title: <label className="titleCard14400">{t('Applicant Name')}</label>,
      dataIndex: 'name',
      key: 'name',
      render: (val, record) => {
        const norm = normalizeSubmission(record, i18n.language);
        const nameVal = (norm?.name && norm.name !== '-') ? norm.name : (record.member?.name || val || '-');
        return <span style={{ color: '#262626', fontSize: 13 }}>{nameVal}</span>;
      },
    },
    {
      title: <label className="titleCard14400">{t('Phone/Email')}</label>,
      dataIndex: 'phone',
      key: 'phone',
      render: (val, record) => {
        const norm = normalizeSubmission(record, i18n.language);
        let phoneVal = norm?.phone && norm.phone !== '-' ? norm.phone : '';
        if (!phoneVal && record?.member?.phone) {
          if (typeof record.member.phone === 'object') {
            const cc = record.member.phone.countryCode
              ? `${String(record.member.phone.countryCode).startsWith('+') ? '' : '+'}${record.member.phone.countryCode} `
              : '';
            phoneVal = `${cc}${record.member.phone.number || ''}`.trim();
          } else {
            phoneVal = String(record.member.phone).trim();
          }
        }
        let emailVal = norm?.email && norm.email !== '-' ? norm.email : (record?.member?.email || record?.email || '');
        const parts = [phoneVal, emailVal].filter(Boolean);
        return <span style={{ color: '#262626', fontSize: 13 }}>{parts.length > 0 ? parts.join(' / ') : '-'}</span>;
      },
    },
    {
      title: <label className="ant-table-column-title titleCard14400">{t('Used Time')}</label>,
      dataIndex: 'usedTS',
      key: 'usedTS',
      render: (val) => <span style={{ color: '#262626', fontSize: 13 }}>{val ? formatDateTime(val) : '-'}</span>,
    },
  ];

  const unusedDetailColumns = [
    {
      title: <label className="titleCard14400">{t('Invitation Code')}</label>,
      dataIndex: 'couponCode',
      key: 'couponCode',
      render: (val, record) => <span style={{ color: '#262626', fontSize: 13, fontWeight: 500 }}>{val || record.code}</span>,
    },
    {
      title: <label className="titleCard14400">{t('Status')}</label>,
      dataIndex: 'status',
      key: 'status',
      render: (val) => (
        <span style={{ color: '#E50A47', fontSize: 13, fontWeight: 500 }}>
          {val ? (t(val.charAt(0).toUpperCase() + val.slice(1)) || val) : (t('Unused'))}
        </span>
      ),
    },
    {
      title: <label className="ant-table-column-title titleCard14400">{t('Creation Time')}</label>,
      dataIndex: 'createdTS',
      key: 'createdTS',
      render: (val) => <span style={{ color: '#262626', fontSize: 13 }}>{val ? formatDateTime(val) : (inviteDetail?.record?.createdTS ? formatDateTime(inviteDetail.record.createdTS) : '-')}</span>,
    },
  ];

  const inviteColumns = [
    {
      title: <label className="titleCard14400">{t('Quantity')}</label>,
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      render: (val) => <span style={{ color: '#262626', fontSize: 13 }}>{val ?? 0}</span>,
    },
    {
      title: <label className="titleCard14400">{t('Unused')}</label>,
      dataIndex: 'unusedQuantity',
      key: 'unusedQuantity',
      render: (val, record) => (
        <span
          onClick={() => {
            setInviteDetail({ record, type: 'unused' });
            setDetailKeyword('');
          }}
          style={{ color: '#0075ff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
        >
          {val ?? 0}
        </span>
      ),
    },
    {
      title: <label className="titleCard14400">{t('Used')}</label>,
      dataIndex: 'usedQuantity',
      key: 'usedQuantity',
      render: (val, record) => (
        <span
          onClick={() => {
            setInviteDetail({ record, type: 'used' });
            setDetailKeyword('');
          }}
          style={{ color: '#0075ff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
        >
          {val ?? 0}
        </span>
      ),
    },
    {
      title: <label className="ant-table-column-title titleCard14400">{t('Creation Time')}</label>,
      dataIndex: 'createdTS',
      key: 'createdTS',
      sorter: true,
      sortOrder: inviteTableParams.sortField === 'createdTS' ? inviteTableParams.sortOrder : null,
      render: (val) => <span style={{ color: '#262626', fontSize: 13 }}>{val ? formatDateTime(val) : '-'}</span>,
    },
     {
      title: <label className="titleCard14400">{t('Remark')}</label>,
      dataIndex: 'remarks',
      key: 'remarks',
      render: (_, record) => <span style={{ color: '#262626', fontSize: 13 }}>{record?.remarks || '-'}</span>,
    },
    {
      title: <label className="titleCard14400">{t('Operator') || t('Action')}</label>,
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Switch
            onChange={() => handleToggleInviteStatus(record)}
            checked={record.isActive}
          />
          <span
            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
            onClick={() => exportSingleInviteToExcel(record)}
            title={t('Export Excel')}
          >
            <ExportExcelListSvg height={22} width={22} />
          </span>
          {!isExpired && record?.unusedQuantity > 0  ? (
            <span
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
              onClick={() => handleRevokeInvite(record)}
              title={t('Revoke')}
            >
              <RevokeSvg width={22} height={22} />
            </span>)
            : null}
        </Space>
      ),
    },
  ];

  const applyStylesToSheet = (worksheet, headerRowIndices, boldCellKeys = []) => {
    if (!worksheet['!ref']) return;
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const borderStyle = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    };

    const rowHeights = [];
    for (let R = range.s.r; R <= range.e.r; ++R) {
      rowHeights[R] = { hpt: 22 };
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) continue;

        const isHeaderRow = headerRowIndices.includes(R);
        const isBoldCell = boldCellKeys.includes(cellAddress);

        worksheet[cellAddress].s = {
          ...worksheet[cellAddress].s,
          border: borderStyle,
          font: isHeaderRow || isBoldCell ? { bold: true } : {},
          alignment: { vertical: 'center', horizontal: 'left' },
        };
      }
    }
    worksheet['!rows'] = rowHeights;
  };

  const autoSizeColumns = (worksheet) => {
    if (!worksheet['!ref']) return;
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const colWidths = [];

    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxWidth = 10;
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = worksheet[cellAddress];
        if (cell && cell.v !== undefined && cell.v !== null) {
          const cellValue = String(cell.v);
          let length = 0;
          for (let i = 0; i < cellValue.length; i++) {
            if (cellValue.charCodeAt(i) > 255) {
              length += 2.2;
            } else {
              length += 1.2;
            }
          }
          if (length > maxWidth) {
            maxWidth = length;
          }
        }
      }
      colWidths[C] = { wch: Math.ceil(maxWidth) + 2 };
    }
    worksheet['!cols'] = colWidths;
  };

  const exportSingleInviteToExcel = async (record) => {
    try {
      const batchId = record._id || record.id;
      // message.loading({ content: t('Preparing Excel export...'), key: 'exporting' });
      const res = await getDataRequest(EVENT_CODE_BATCHES_USED_URL, {
        batchId: batchId,
        status: 'ALL',
        pageNo: 1,
        pageSize: 1000,
      });
      message.destroy('exporting');
      const list = (res && (Array.isArray(res.data) ? res.data : res.data?.list)) || [];

      const aoa = [
        [t('Total Quantity'), `${record?.totalQuantity ?? 0}`, t('Status'), record?.isActive ? (t('Active')) : (t('Disabled'))],
        [t('Unused'), `${record?.unusedQuantity ?? 0}`, t('Used'), `${record?.usedQuantity ?? 0}`],
        [t('Creation Time'), record?.createdTS ? formatDateTime(record.createdTS) : '', t('Batch Remarks'), record?.remarks || '-'],
        [],
        [
          t('Invitation Code'),
          t('Status'),
          t('Applicant Name'),
          t('Phone Number'),
          t('Email'),
          t('Salon / Company'),
          t('Used Time'),
        ],
      ];

      if (list.length > 0) {
        list.forEach((rawItem) => {
          const norm = normalizeSubmission(rawItem, i18n.language);
          let p = norm?.phone && norm.phone !== '-' ? norm.phone : '';
          if (!p && rawItem?.member?.phone) {
            if (typeof rawItem.member.phone === 'object') {
              const cc = rawItem.member.phone.countryCode
                ? `${String(rawItem.member.phone.countryCode).startsWith('+') ? '' : '+'}${rawItem.member.phone.countryCode} `
                : '';
              p = `${cc}${rawItem.member.phone.number || ''}`.trim();
            } else {
              p = String(rawItem.member.phone).trim();
            }
          }
          let e = norm?.email && norm.email !== '-' ? norm.email : (rawItem?.member?.email || rawItem?.email || '');
          let n = norm?.name && norm.name !== '-' ? norm.name : (rawItem?.member?.name || rawItem?.name || '-');
          let c = norm?.company && norm.company !== '-' ? norm.company : (rawItem?.member?.company || rawItem?.company || '-');

          aoa.push([
            rawItem?.couponCode || rawItem?.code || norm?.code || '',
            rawItem?.status ? (t(rawItem.status.charAt(0).toUpperCase() + rawItem.status.slice(1)) || rawItem.status) : '',
            n,
            p || '-',
            e || '-',
            c,
            rawItem?.usedTS ? formatDateTime(rawItem.usedTS) : '-',
          ]);
        });
      }

      const worksheet = XLSX.utils.aoa_to_sheet(aoa);
      applyStylesToSheet(worksheet, [4], ['A1', 'C1', 'A2', 'C2', 'A3', 'C3']);
      autoSizeColumns(worksheet);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      const safeDate = record?.createdTS ? record.createdTS.replace(/[: ]/g, '_').slice(0, 19) : record?.id;
      XLSX.writeFile(workbook, `邀请码明细_${safeDate}.xlsx`);
      // message.success(t('Exported successfully!'));
    } catch (err) {
      message.destroy('exporting');
      console.log('Export error:', err);
      message.error(t('Export failed'));
    }
  };

  const handleExport = async () => {
    if (approvalTab === 'confirmed') {
      const aoa = [
        [
          t('Total Quantity'),
          t('Unused'),
          t('Used'),
          t('Creation Time'),
          t('Status'),
        ],
      ];
      inviteNumbers.forEach((rec) => {
        aoa.push([
          rec.totalQuantity ?? 0,
          rec.unusedQuantity ?? 0,
          rec.usedQuantity ?? 0,
          rec.createdTS ? formatDateTime(rec.createdTS) : '-',
          rec.isActive ? (t('Active')) : (t('Disabled')),
        ]);
      });
      const worksheet = XLSX.utils.aoa_to_sheet(aoa);
      applyStylesToSheet(worksheet, [0]);
      autoSizeColumns(worksheet);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      XLSX.writeFile(workbook, `邀请码列表_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } else {
      try {
        // message.loading({ content: t('Preparing Excel export...'), key: 'exporting' });
        const params = {
          keyword: searchKeyword ? searchKeyword.trim() : '',
          startDate: '',
          endDate: '',
          pageNo: 1,
          pageSize: 10000,
          eventsId: effectiveEventId || '',
        };
        const res = await getDataRequest(EVENT_SUBMISSIONS_URL, params);
        message.destroy('exporting');
        const list = (res && (Array.isArray(res.data) ? res.data : res.data?.list)) || [];
        const normalizedList = list.map((item) => normalizeSubmission(item, i18n.language));

        const aoa = [
          [
            t('Applicant Name'),
            t('Phone Number'),
            t('Salon / Company'),
            t('Invitation Code'),
            t('Email'),
            t('Registration Time'),
          ],
        ];
        normalizedList.forEach((app) => {
          aoa.push([
            app.name || '-',
            app.phone || '-',
            app.company || '-',
            app.localize?.cn?.code || app.code || '-',
            app.email || '-',
            app.regTime || '-',
          ]);
        });
        const worksheet = XLSX.utils.aoa_to_sheet(aoa);
        applyStylesToSheet(worksheet, [0]);
        autoSizeColumns(worksheet);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        XLSX.writeFile(workbook, `报名审批列表_${new Date().toISOString().slice(0, 10)}.xlsx`);
        // message.success(t('Exported registration approval Excel spreadsheet!'));
      } catch (err) {
        message.destroy('exporting');
        console.error('Export error:', err);
        message.error(t('Export failed'));
      }
    }
  };

  const handleExportDetail = () => {
    const isUsed = inviteDetail?.type === 'used';
    const aoa = isUsed
      ? [
        [
          t('Invitation Code'),
          t('Applicant Name'),
          t('Phone Number'),
          t('Email'),
          t('Salon / Company'),
          t('Used Time'),
        ],
      ]
      : [
        [
          t('Invitation Code'),
          t('Status'),
          t('Creation Time'),
        ],
      ];

    detailData.forEach((rawItem) => {
      if (isUsed) {
        const norm = normalizeSubmission(rawItem, i18n.language);
        let p = norm?.phone && norm.phone !== '-' ? norm.phone : '';
        if (!p && rawItem?.member?.phone) {
          if (typeof rawItem.member.phone === 'object') {
            const cc = rawItem.member.phone.countryCode
              ? `${String(rawItem.member.phone.countryCode).startsWith('+') ? '' : '+'}${rawItem.member.phone.countryCode} `
              : '';
            p = `${cc}${rawItem.member.phone.number || ''}`.trim();
          } else {
            p = String(rawItem.member.phone).trim();
          }
        }
        let e = norm?.email && norm.email !== '-' ? norm.email : (rawItem?.member?.email || rawItem?.email || '');
        let n = norm?.name && norm.name !== '-' ? norm.name : (rawItem?.member?.name || rawItem?.name || '-');
        let c = norm?.company && norm.company !== '-' ? norm.company : (rawItem?.member?.company || rawItem?.company || '-');

        aoa.push([
          rawItem.couponCode || rawItem.code || norm?.code || '',
          n,
          p || '-',
          e || '-',
          c,
          rawItem.usedTS ? formatDateTime(rawItem.usedTS) : '',
        ]);
      } else {
        aoa.push([
          rawItem.couponCode || rawItem.code || '',
          rawItem.status ? (t(rawItem.status.charAt(0).toUpperCase() + rawItem.status.slice(1)) || rawItem.status) : (t('Unused')),
          rawItem.createdTS ? formatDateTime(rawItem.createdTS) : (inviteDetail?.record?.createdTS ? formatDateTime(inviteDetail.record.createdTS) : ''),
        ]);
      }
    });

    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    applyStylesToSheet(worksheet, [0]);
    autoSizeColumns(worksheet);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const titleName = isUsed ? '已使用邀请码' : '未使用邀请码';
    XLSX.writeFile(workbook, `${titleName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    // message.success(t('Exported successfully!'));
  };

  const handleJump = () => {
    const p = parseInt(jumpPage, 10);
    const maxPage = Math.ceil(totalSubmissions / pageSize) || 1;
    if (p && p > 0 && p <= maxPage) {
      setCurrentPage(p);
      fetchSubmissions(p, pageSize, searchKeyword);
    } else {
      message.warning(t('Please enter a valid page number.'));
    }
  };

  const tabItems = [
    { key: 'pending', label: t('Registration Approval') },
    { key: 'confirmed', label: t('Invite Number') },
  ];

  return (
    <HeaderComponent
      isHide="view"
      pageTitle={
        inviteDetail ? (
          <span
            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            onClick={() => {
              setInviteDetail(null)
              fetchInviteBatches()
            }}
          >
            <ArrowLeftOutlined /> {t('Registration Approval')} / {inviteDetail.type === 'used' ? (t('Used Invitation Codes')) : (t('Unused Invitation Codes'))}
          </span>
        ) : (
          <span
            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            onClick={() => navigate('/events')}
          >
            <ArrowLeftOutlined /> {t('Registration Approval')}
          </span>
        )
      }
    >
      {inviteDetail ? (
        <div className="box-shadow h-100 w-100 event-page-wrapper">
          <div className="w-100 h-100 overflow-auto">
            <div className="event-subpage-container">
              {/* Search filter bar */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 16,
                }}
              >
                <Input
                  prefix={<SearchSvg />}
                  placeholder={t('Search')}
                  value={detailKeyword}
                  onChange={(e) => setDetailKeyword(e.target.value)}
                  style={{ borderRadius: 4, height: 32, borderColor: '#d9d9d9', width: 320 }}
                  allowClear
                />

                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <Button
                    style={{
                      height: 32,
                      padding: '0 18px',
                      borderRadius: 4,
                      borderColor: '#D9D9D9',
                      color: '#262626',
                      background: '#FFFFFF',
                    }}
                    onClick={() => {
                      fetchInviteDetails();
                    }}
                  >
                    {t('Query')}
                  </Button>
                  <div style={{ alignSelf: 'stretch', borderLeft: '1px solid #e2e2e2' }}></div>
                  <div
                    onClick={handleExportDetail}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      padding: 4,
                      borderRadius: 4,
                    }}
                    title={t('Export')}
                  >
                    <ExportExcelSvg />
                  </div>
                </div>
              </div>

              {/* Detail Table */}
              <div className="w-100">
                <TableFetchData
                  tableParams={detailTableParams}
                  setTableParams={setDetailTableParams}
                  data={detailData}
                  loading={detailLoading}
                  setLoading={setDetailLoading}
                  columns={inviteDetail.type === 'used' ? usedDetailColumns : unusedDetailColumns}
                  rowKey={(r) => r._id || r.id || r.couponCode || r.code}
                  x="100%"
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="box-shadow h-100 w-100 event-page-wrapper">
          <div className="w-100 h-100 overflow-auto">
            <div className="event-subpage-container">
              {/* 2-Column Layout */}
              <div className="event-subpage-layout">
                {/* Left Column: Compact Event Summary Card */}
                <div className="event-summary-col">
                  <Spin spinning={eventLoading}>
                    <div className="event-summary-card">
                      <div className="event-summary-header">
                        <img
                          src={eventPoster}
                          alt={eventTitle}
                          className="thumb"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DEFAULT_POSTER;
                          }}
                        />
                        <h3 className="title">{eventTitle}</h3>
                      </div>

                      <div className="event-summary-meta">
                        <div style={{ color: '#1f2937' }}>{t('Event Time')}: {eventTime}</div>
                        <div>{t('Creator')}: {eventCreator}</div>
                      </div>

                      <div className="event-summary-bottom">
                        <span
                          className={isExpired ? 'tag-expired' : 'tag-active'}
                          style={{
                            borderRadius: 12,
                            padding: '2px 10px',
                            backgroundColor: isExpired ? '#F3F4F6' : undefined,
                            color: isExpired ? '#6B7280' : undefined,
                          }}
                        >
                          {isExpired ? (t('Expired')) : (t('Ongoing'))}
                        </span>
                        <div className="stat-red">
                          <span style={{ color: '#E50A47' }}>{t('Registered Count')}: </span>
                          <span style={{ fontWeight: 600, color: '#E50A47' }}>{registeredCount}</span>
                        </div>
                      </div>
                    </div>
                  </Spin>
                </div>

                {/* Right Column: Applicants Management */}
                <div className="event-main-content-col">
                  {/* Sub-tabs */}
                  {console.log("approvalTab", approvalTab)}
                  <Tabs
                    activeKey={approvalTab}
                    onChange={(key) => {
                      setApprovalTab(key);
                      setCurrentPage(1);
                      setJumpPage('1');
                      setInviteDetail(null);
                      if (key === 'confirmed') {
                        setInviteTableParams((prev) => ({
                          ...prev,
                          pagination: { ...prev.pagination, current: 1 },
                          sortOrder: undefined,
                          sortField: undefined,
                        }));
                      }
                    }}
                    className="cusStyle"
                    items={tabItems}
                    tabBarExtraContent={
                      !isExpired && approvalTab === "confirmed" ? (
                        <Button
                          style={{
                            fontSize: 16,
                            backgroundColor: "#F8E2E8",
                            border: "none",
                            color: "#E50A47",
                            marginRight: "1.5rem"
                          }}
                          danger
                          onClick={() => setCreateModalOpen(true)}
                        >
                          {t("Create Invite Number")}
                        </Button>
                      ) : null
                    }
                  />

                  {/* Search Filter Bar */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 16,
                    }}
                  >
                    <Input
                      prefix={<SearchSvg />}
                      placeholder={t('Search')}
                      value={searchKeyword}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSearchKeyword(val);
                        if (!val) {
                          if (approvalTab === 'pending') {
                            setCurrentPage(1);
                            setJumpPage('1');
                            fetchSubmissions(1, pageSize, '');
                          }
                        }
                      }}
                      onPressEnter={() => {
                        if (approvalTab === 'confirmed') {
                          fetchInviteBatches();
                        } else {
                          setCurrentPage(1);
                          setJumpPage('1');
                          fetchSubmissions(1, pageSize, searchKeyword);
                        }
                      }}
                      style={{ borderRadius: 4, height: 32, borderColor: '#d9d9d9', width: 320 }}
                      allowClear
                    />

                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <Button
                        style={{
                          height: 32,
                          padding: '0 18px',
                          borderRadius: 4,
                          borderColor: '#D9D9D9',
                          color: '#262626',
                          background: '#FFFFFF',
                        }}
                        onClick={() => {
                          if (approvalTab === 'confirmed') {
                            fetchInviteBatches();
                          } else {
                            setCurrentPage(1);
                            setJumpPage('1');
                            fetchSubmissions(1, pageSize, searchKeyword);
                          }
                        }}
                      >
                        {t('Query')}
                      </Button>
                      <div style={{ alignSelf: 'stretch', borderLeft: '1px solid #e2e2e2' }}></div>
                      <div
                        onClick={handleExport}
                        style={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          padding: 4,
                          borderRadius: 4,
                        }}
                        title={t('Export')}
                      >
                        <ExportExcelSvg />
                      </div>
                    </div>
                  </div>

                  {/* Conditional View: Invite Number Table OR Applicant Cards Grid */}
                  {approvalTab === 'confirmed' ? (
                    <div className="w-100">
                      <TableFetchData
                        tableParams={inviteTableParams}
                        setTableParams={setInviteTableParams}
                        data={inviteNumbers}
                        loading={inviteLoading}
                        setLoading={setInviteLoading}
                        columns={inviteColumns}
                        rowKey={(r) => r._id || r.id}
                        x="100%"
                      />
                    </div>
                  ) : (
                    <>
                      {/* 2-Column Grid of Applicant Cards */}
                      <Spin spinning={submissionsLoading}>
                        <div className="applicant-cards-grid" style={{ minHeight: 200 }}>
                          {submissions.map((applicant) => (
                            <div key={applicant.id || applicant._id} className="applicant-card">
                              <div className="applicant-info-row">
                                <span className="applicant-info-label">{t('Applicant Name')} :</span>
                                <span className="applicant-info-value">{applicant.name}</span>
                              </div>
                              <div className="applicant-info-row">
                                <span className="applicant-info-label">{t('Phone Number')} :</span>
                                <span className="applicant-info-value">{applicant.phone}</span>
                              </div>
                              <div className="applicant-info-row">
                                <span className="applicant-info-label">{t('Salon / Company')} :</span>
                                <span className="applicant-info-value">{applicant.company}</span>
                              </div>
                              <div className="applicant-info-row">
                                <span className="applicant-info-label">{t('Invitation Code')} :</span>
                                <span className="applicant-info-value">{applicant.localize?.cn?.code || applicant.code || '-'}</span>
                              </div>
                              <div className="applicant-info-row">
                                <span className="applicant-info-label">{t('Email')} :</span>
                                <span className="applicant-info-value">{applicant.email}</span>
                              </div>
                              <div className="applicant-info-row">
                                <span className="applicant-info-label">{t('Registration Time')} :</span>
                                <span className="applicant-info-value">{applicant.regTime}</span>
                              </div>
                            </div>
                          ))}

                          {!submissionsLoading && submissions.length === 0 && (
                            <div
                              style={{
                                gridColumn: '1 / -1',
                                textAlign: 'center',
                                padding: '40px 0',
                                color: '#636E88',
                              }}
                            >
                              {t('No applicant records')}
                            </div>
                          )}
                        </div>
                      </Spin>

                      {/* Bottom Pagination */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 16,
                          marginTop: 32,
                          paddingTop: 16,
                        }}
                      >
                        <Select
                          value={pageSize}
                          onChange={(val) => {
                            setPageSize(val);
                            setCurrentPage(1);
                            setJumpPage('1');
                            fetchSubmissions(1, val, searchKeyword);
                          }}
                          style={{ width: 90 }}
                          options={[
                            { value: 4, label: `4${t('per page')}` },
                            { value: 8, label: `8${t('per page')}` },
                            { value: 10, label: `10${t('per page')}` },
                            { value: 16, label: `16${t('per page')}` },
                            { value: 20, label: `20${t('per page')}` },
                          ]}
                        />

                        <Pagination
                          current={currentPage}
                          pageSize={pageSize}
                          total={totalSubmissions}
                          onChange={(page) => {
                            setCurrentPage(page);
                            setJumpPage(String(page));
                            fetchSubmissions(page, pageSize, searchKeyword);
                          }}
                          showSizeChanger={false}
                        />

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#636E88' }}>
                          <span>{t('Jump to')}</span>
                          <Input
                            style={{ width: 44, height: 32, textAlign: 'center', borderRadius: 4 }}
                            value={jumpPage}
                            onChange={(e) => setJumpPage(e.target.value)}
                            onPressEnter={handleJump}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <CreateInviteBatchModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          fetchInviteBatches();
        }}
        eventId={effectiveEventId}
        eventTitle={eventTitle}
        eventStartDate={eventDetail?.startDate}
        eventEndDate={eventDetail?.endDate}
      />
      <MessageConfirmModal textCentered ref={messageModalRef} />
    </HeaderComponent>
  );
};

export default EventApprovalPage;
